import { useEffect, useMemo, useRef, useState } from 'react';
import { Global } from '@emotion/react';
import { Modal } from 'antd';
import { CloseOutlined } from '@ant-design/icons';

import AxisConfigurationModalFooter from 'components/molecules/PackArchitecture/AxisConfigurationModalFooter';
import { getConfigurationTitle, getDiagnosticPageName } from 'components/molecules/PackArchitecture/axisConfigurationModal.constants';
import {
    AXIS_CONFIGURATION_MODAL_CLASS,
    axisConfigurationModalGlobalStyles,
    BINNING_MODAL,
} from 'components/molecules/PackArchitecture/AxisConfigurationModal.styles';
import KpiAxisConfigurationModalConfigurationControls from 'components/molecules/PackArchitecture/KpiAxisConfigurationModalConfigurationControls';
import KpiAxisConfigurationModalPreview from 'components/molecules/PackArchitecture/KpiAxisConfigurationModalPreview';
import {
    type KpiAxisConfiguration,
    type KpiBinLabelAffixes,
    createDefaultKpiAxisConfiguration,
    getUniformBinSize,
    hasKpiBinSizeError,
    kpiConfigsAreEqual,
    type KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';
import { DEFAULT_KPI_DATA_RANGE } from 'constants/ppa';
import type { KpiAxisControlsMeta } from 'components/molecules/PackArchitecture/kpiAxisControlsMeta.types';
import { getKpiAxisConfigurationDefaultsFromMeta } from 'components/pages/PackArchitecture/packArchitectureMeta.helpers';
import { useWhitespaceKpiAxisConfigurationPreview } from 'hooks/useWhitespaceKpiAxisConfigurationPreview';
import { usePpaKpiAxisConfigurationPreview } from 'hooks/usePpaKpiAxisConfigurationPreview';
import type { PricePackArchitectureRequest } from 'hooks/ppa/useFetchPricePackArchitecture';
import { PPA_KPI_AXIS_CONFIG_MODAL } from 'constants/ppaTestIds';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    PPA_KPI_AXIS_CONFIG_APPLIED,
    PPA_KPI_AXIS_CONFIG_CANCELLED,
    PPA_KPI_AXIS_CONFIG_RESET,
} from 'utils/mixpanelEventNames';

export type {
    KpiAxisConfiguration,
    KpiDataRange,
} from 'components/molecules/PackArchitecture/kpiAxisConfiguration.utils';

type KpiAxisConfigurationModalProps = {
    open: boolean;
    kpiLabel: string;
    controlsMeta?: KpiAxisControlsMeta | null;
    initialConfig?: KpiAxisConfiguration;
    dataRange?: KpiDataRange;
    binLabelAffixes?: KpiBinLabelAffixes;
    chartRequest?: PricePackArchitectureRequest | null;
    whitespacePreviewRequest?: Record<string, unknown> | null;
    useWhitespacePreview?: boolean;
    onClose: () => void;
    onApply?: (config: KpiAxisConfiguration) => void;
};

const KpiAxisConfigurationModal = ({
    open,
    kpiLabel,
    controlsMeta,
    initialConfig,
    dataRange = DEFAULT_KPI_DATA_RANGE,
    binLabelAffixes,
    chartRequest = null,
    whitespacePreviewRequest = null,
    useWhitespacePreview = false,
    onClose,
    onApply,
}: KpiAxisConfigurationModalProps) => {
    const pageName = getDiagnosticPageName(useWhitespacePreview);

    const configurationDefaults = useMemo(() => getKpiAxisConfigurationDefaultsFromMeta(controlsMeta), [controlsMeta]);
    const appliedConfig = useMemo(
        () => initialConfig ?? createDefaultKpiAxisConfiguration(dataRange, configurationDefaults),
        [configurationDefaults, dataRange, initialConfig],
    );
    const defaultConfig = useMemo(
        () => createDefaultKpiAxisConfiguration(dataRange, configurationDefaults),
        [configurationDefaults, dataRange],
    );

    const [config, setConfig] = useState<KpiAxisConfiguration>(appliedConfig);
    const [baselineConfig, setBaselineConfig] = useState<KpiAxisConfiguration>(appliedConfig);
    const [pauseKpiPreview, setPauseKpiPreview] = useState(false);
    const wasOpenRef = useRef(false);

    const whitespacePreview = useWhitespaceKpiAxisConfigurationPreview({
        open: open && useWhitespacePreview,
        gridRequest: whitespacePreviewRequest,
        previewConfig: config,
        pauseFetch: pauseKpiPreview,
    });

    const ppaPreview = usePpaKpiAxisConfigurationPreview({
        open: open && !useWhitespacePreview && Boolean(chartRequest),
        chartRequest,
        previewConfig: config,
        pauseFetch: pauseKpiPreview,
    });

    const useApiPreview = useWhitespacePreview || Boolean(chartRequest);
    const previewLabels = useWhitespacePreview ? whitespacePreview.previewLabels : ppaPreview.previewLabels;
    const isPreviewLoading = useWhitespacePreview
        ? whitespacePreview.isLoading && !pauseKpiPreview
        : useApiPreview
        ? ppaPreview.isLoading && !pauseKpiPreview
        : false;

    useEffect(() => {
        if (open && !wasOpenRef.current) {
            setConfig(appliedConfig);
            setBaselineConfig(appliedConfig);
            setPauseKpiPreview(false);
        }
        wasOpenRef.current = open;
    }, [open, appliedConfig]);

    const hasApplyChanges = useMemo(() => !kpiConfigsAreEqual(config, baselineConfig), [baselineConfig, config]);

    const hasResetChanges = useMemo(() => !kpiConfigsAreEqual(config, defaultConfig), [config, defaultConfig]);

    const hasBinCountError = useMemo(
        () => config.binningMethod === 'uniform' && hasKpiBinSizeError(config.min, config.max, config.binCount),
        [config.binCount, config.binningMethod, config.max, config.min],
    );

    const updateConfig = (patch: Partial<KpiAxisConfiguration>) => {
        setConfig((current) => ({ ...current, ...patch }));
    };

    const handleReset = () => {
        setConfig(defaultConfig);
        trackEventMixpanel(PPA_KPI_AXIS_CONFIG_RESET, { page_name: pageName, axis_name: kpiLabel });
    };

    const handleCancel = () => {
        setConfig(baselineConfig);
        onClose();
        trackEventMixpanel(PPA_KPI_AXIS_CONFIG_CANCELLED, { page_name: pageName, axis_name: kpiLabel });
    };

    const handleApply = () => {
        if (hasBinCountError) return;
        setBaselineConfig(config);
        onApply?.(config);
        onClose();
        trackEventMixpanel(PPA_KPI_AXIS_CONFIG_APPLIED, {
            page_name: pageName,
            axis_name: kpiLabel,
            previous_range_min: baselineConfig.min,
            selected_range_min: config.min,
            previous_range_max: baselineConfig.max,
            selected_range_max: config.max,
            range_method: 'both',
            ...(config.binningMethod === 'uniform'
                ? {
                      previous_bin_count: baselineConfig.binCount,
                      selected_bin_count: config.binCount,
                      previous_bin_size: getUniformBinSize(
                          baselineConfig.min,
                          baselineConfig.max,
                          baselineConfig.binCount,
                      ),
                      selected_bin_size: getUniformBinSize(config.min, config.max, config.binCount),
                  }
                : {
                      breakpoint_count: config.breakpoints.length,
                      breakpoints: config.breakpoints,
                      custom_binning_method: 'both',
                  }),
        });
    };

    return (
        <>
            <Global styles={axisConfigurationModalGlobalStyles} />
            <Modal
                title={getConfigurationTitle(kpiLabel)}
                open={open}
                onCancel={handleCancel}
                closeIcon={<CloseOutlined className="text-sm text-toolbarLabelGrey" />}
                width={720}
                footer={
                    <AxisConfigurationModalFooter
                        hasApplyChanges={hasApplyChanges && !hasBinCountError}
                        hasResetChanges={hasResetChanges}
                        onReset={handleReset}
                        onCancel={handleCancel}
                        onApply={handleApply}
                    />
                }
                destroyOnClose
                centered
                wrapClassName={`${AXIS_CONFIGURATION_MODAL_CLASS} ${BINNING_MODAL}`}
                modalRender={(modalNode) => <div data-testid={PPA_KPI_AXIS_CONFIG_MODAL}>{modalNode}</div>}
            >
                <div className="flex h-full min-h-0">
                    {controlsMeta ? (
                        <KpiAxisConfigurationModalConfigurationControls
                            config={config}
                            controlsMeta={controlsMeta}
                            dataRange={dataRange}
                            defaultBinCount={
                                configurationDefaults?.binCount ?? controlsMeta.bins.uniform.count.defaultValue
                            }
                            binLabelAffixes={binLabelAffixes}
                            onChange={updateConfig}
                            onKpiPreviewPauseChange={setPauseKpiPreview}
                        />
                    ) : null}
                    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-axisModalBg pl-6 pt-4">
                        <KpiAxisConfigurationModalPreview
                            config={config}
                            binLabelAffixes={binLabelAffixes}
                            previewLabels={useApiPreview ? previewLabels : undefined}
                            isLoading={isPreviewLoading}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default KpiAxisConfigurationModal;
